/**
 * Shared skin for text fields (Input, Textarea, Select): same height, padding,
 * radius, border and focus recipe. 16 px text with a finger (iOS does not
 * zoom), 14 px with a mouse in the wide layout. Focus = brand border plus a
 * soft halo, drawn at once and let go in 120 ms (spec 07 › Foco e
 * interacción); `outline-hidden` keeps a transparent outline that Windows high
 * contrast turns visible. Disabled is an explicit grey field, never opacity:
 * it paints after 150 ms (`delay-indicator`, a save that answers sooner never
 * greys the form) and fades back at once. With a mouse a hover darkens the
 * border at once and lets go in 120. `ui-control` hooks the browser's
 * autofill wash (globals.css: the filled fields turn brand-subtle together
 * instead of the browser's blue or yellow).
 */
export const controlBase =
  "ui-control w-full rounded-md border border-input bg-background px-3 py-2 text-base text-foreground shadow-xs transition-[border-color,box-shadow,background-color,color] duration-fast ease-enter focus-visible:duration-0 md:pointer-fine:hover:not-focus-visible:not-disabled:not-aria-invalid:border-muted-foreground md:pointer-fine:hover:transition-none placeholder:text-placeholder md:pointer-fine:text-sm focus-visible:border-ring focus-visible:outline-hidden focus-visible:ring-[3px] focus-visible:ring-ring/20 disabled:cursor-not-allowed disabled:border-border disabled:bg-disabled disabled:text-disabled-foreground disabled:delay-indicator aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive/20";

/**
 * Focus recipe for a field shell that wraps a bare input (composer, tag and
 * city inputs, the currency + amount pair): the same brand border and 3 px
 * halo as `controlBase`, on `:focus-within` (a11y-06). Pair it with
 * `data-field-focus` on the shell, which draws a real outline in forced-colors
 * mode, and `outline-hidden` (never `outline-none`) on the inner input.
 */
export const fieldShellFocus =
  "transition-[border-color,box-shadow] duration-fast ease-enter focus-within:duration-0 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/20";

/**
 * Field heights that match Button (components-15): with a finger every field
 * is 44 px; with a mouse in the wide layout `sm` is 32 px and `md` 36 px, so
 * a field and the button beside it (a `sm` or `md` Button) line up. `default`
 * is 44 px everywhere, the long-form fields of onboarding and auth.
 */
export type ControlSize = "sm" | "md" | "default";

export const controlSizes: Record<ControlSize, string> = {
  sm: "min-h-11 md:pointer-fine:min-h-8 md:pointer-fine:py-1",
  md: "min-h-11 md:pointer-fine:min-h-9 md:pointer-fine:py-1.5",
  default: "min-h-11",
};
